// 감사 로그 표시 도우미 — raw 코드/UUID를 사람이 읽을 수 있게 바꾼다.
// 행위자(UUID→이름/이메일)는 서버가 해석해 실어 주고, 여기서는 순수 표시 변환만 한다:
// action 코드 → 한국어 라벨, target_type → 한국어 라벨, 긴 식별자 → 짧은 표기.

import type { AuditLog } from '~/api/types'

// action 코드 → 짧은 한국어 라벨. webapp 라우터의 record(action=...) 전수와 대응한다.
// 알 수 없는 코드는 raw 코드를 그대로 노출한다(actionLabel의 fallback).
const ACTION_LABELS: Record<string, string> = {
    // 문서
    'document.upload': '문서 업로드',
    'document.remove': '문서 삭제',
    'document.reindex': '문서 재색인',
    'document.curation_lock': '문서 큐레이션 잠금',
    'document.bulk_reindex': '문서 일괄 재색인',
    'document.bulk_delete': '문서 일괄 삭제',
    // 사용자
    'user.update_role': '역할 변경',
    'user.update_status': '가입 상태 변경',
    'user.reset_password': '비밀번호 재설정',
    'user.bulk_update_role': '역할 일괄 변경',
    // 모델 레지스트리
    'model.create': '모델 등록',
    'model.update': '모델 수정',
    'model.delete': '모델 삭제',
    'model.bind_use_case': '모델 용도 바인딩',
    'model.unbind_use_case': '모델 용도 해제',
    'model.test_connection': '모델 연결 테스트',
    'model.test': '모델 테스트',
    // 카탈로그(체크리스트)
    'catalog.import': '카탈로그 가져오기',
    'catalog.item.create': '카탈로그 항목 생성',
    'catalog.item.update': '카탈로그 항목 수정',
    'catalog.field.question.update': '카탈로그 질문 수정',
    'catalog.bulk_update': '카탈로그 일괄 수정',
    'catalog.questions.generate': '카탈로그 질문 생성',
    'catalog.revision.apply': '카탈로그 개정 적용',
    // 지식 코퍼스
    'corpus.add_chunk': '코퍼스 청크 추가',
    'corpus.edit_chunk': '코퍼스 청크 편집',
    'corpus.delete_chunk': '코퍼스 청크 삭제',
    // 프롬프트 / 에이전트
    'prompt.create_version': '프롬프트 버전 생성',
    'prompt.activate': '프롬프트 활성화',
    'agent.update': '에이전트 설정 변경',
    // 알림 설정
    'notification.update_email': '이메일 설정 변경',
    'notification.test_email': '테스트 메일 발송',
    // 모니터링 제어
    'monitoring.alert_status': '모니터링 알림 상태 변경',
    'monitoring.notify_settings': '모니터링 알림 설정 변경',
    'monitoring.reading_delete': '계측값 삭제',
    'monitoring.reading_update': '계측값 수정',
    'monitoring.token_revoke': '수집 토큰 폐기',
    // 인증평가 제어
    'assessment.delete': '인증평가 삭제',
    'assessment.rescore': '인증평가 재채점',
    'assessment.report_regenerate': '인증평가 리포트 재생성',
    // 내보내기
    'export.conversations': '상담 내보내기',
}

// target_type → 한국어 라벨. webapp 라우터의 record(target_type=...) 전수와 대응한다.
const TARGET_TYPE_LABELS: Record<string, string> = {
    document: '문서',
    user: '사용자',
    model: '모델',
    model_binding: '모델 바인딩',
    checklist_item: '체크리스트 항목',
    checklist_item_field: '체크리스트 필드',
    corpus_chunk: '코퍼스 청크',
    prompt: '프롬프트',
    agent_config: '에이전트 설정',
    notification_setting: '알림 설정',
    monitoring_dashboard: '모니터링',
    assessment: '인증평가',
    export: '내보내기',
}

/** action 코드를 한국어 라벨로. 미매핑 코드는 raw 코드를 그대로 반환한다. */
export function actionLabel(action: string): string {
    return ACTION_LABELS[action] ?? action
}

/** target_type을 한국어 라벨로. 미매핑 유형은 raw 값을 그대로 반환한다. */
export function targetTypeLabel(type: string): string {
    return TARGET_TYPE_LABELS[type] ?? type
}

/** 행위자 표시명 — 이름 > 이메일 > 짧은 id > '시스템' 순으로 대체한다. */
export function actorLabel(
    log: Pick<AuditLog, 'actor' | 'actorName' | 'actorEmail'>
): string {
    if (log.actorName) return log.actorName
    if (log.actorEmail) return log.actorEmail
    if (!log.actor) return '시스템'
    return shortId(log.actor)
}

/** 긴 식별자(UUID 등)를 앞 8자 + …로 줄인다. 짧으면 그대로 둔다. */
export function shortId(id: string | null | undefined): string {
    if (!id) return ''
    return id.length > 12 ? `${id.slice(0, 8)}…` : id
}

/** 대상 표기 — "유형 · 짧은id". id가 없으면 유형만. */
export function targetLabel(
    log: Pick<AuditLog, 'targetType' | 'targetId'>
): string {
    const type = targetTypeLabel(log.targetType)
    const id = shortId(log.targetId)
    return id ? `${type} · ${id}` : type
}
